import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { mutateFormData } from "@/core/api/api-methods";
import {
  invalidateAfterMutation,
  payrollBatchCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { payrollEndpoints } from "@/features/payroll/api/endpoints";
import type {
  PayrollBatchDto,
  PayrollBatchImportFormData,
} from "@/features/payroll/api/dto";

/**
 * Import d'un classeur `.xlsx` (`POST /PayrollBatches/import`, multipart :
 * `file`, `label`, `feePayer`) → crée une paie en préparation (`DRAFT`).
 * En-têtes reconnus dans les 10 premières lignes, casse et accents ignorés :
 * nom complet (ou prénom + nom), téléphone, montant ou salaire, matricule
 * facultatif. Les autres colonnes sont ignorées. Codes : 201 ; 400 ; 422 ;
 * 503. Pas d'idempotence côté collection v4.1.
 */
export const useImportPayrollBatch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["payroll", "payrollBatches", "import"],
    mutationFn: ({ file, label, feePayer }: PayrollBatchImportFormData) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("label", label);
      formData.append("feePayer", feePayer);
      return mutateFormData<PayrollBatchDto>({
        url: payrollEndpoints.payrollBatches.import,
        formData,
        method: "POST",
      });
    },
    onSuccess: () =>
      invalidateAfterMutation(queryClient, payrollBatchCacheKeys(), {
        resetListPage: true,
      }),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useImportPayrollBatch failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "payroll" }
      );
    },
  });
};
